import { toast } from "$global-providers/toast-provider";
import { Box, Button, styled, TextField, Typography } from "@material-ui/core";
import {
  EpocusBillingModifierOverride,
  EpocusProgramClinicalSetting,
  EpocusWorksheetSet,
  type UpdateEpocusProgramInput,
} from "elephant-web/gql";
import { useState } from "react";
import { useBillingCodesEnabled } from "../../../_lib/billing/use-billing-codes-enabled";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../_lib/epocus-palette";
import { useIsEpocusPlatformAdmin } from "../../../_lib/rbac/epocus-claim";
import { EnumChipSelect } from "../../../_lib/worksheets/worksheet-primitives";
import { SettingsCard, SettingsCardLabel } from "../../_lib/settings-card";
import {
  type EpocusProgramConfigQuery,
  useEpocusUpdateProgramSetupMutation,
} from "./__generated__/program-config-operations.gql";

const WORKSHEET_SET_OPTIONS: { value: EpocusWorksheetSet; label: string }[] = [
  { value: EpocusWorksheetSet.Standard, label: "Standard" },
  { value: EpocusWorksheetSet.Us, label: "US" },
];

const CLINICAL_SETTING_OPTIONS: {
  value: EpocusProgramClinicalSetting;
  label: string;
}[] = [
  { value: EpocusProgramClinicalSetting.Office, label: "Office" },
  {
    value: EpocusProgramClinicalSetting.HospitalOutpatient,
    label: "Hospital outpatient",
  },
  { value: EpocusProgramClinicalSetting.Ed, label: "ED" },
  { value: EpocusProgramClinicalSetting.Asc, label: "ASC" },
];

const FOLLOW_SETTING = "follow_setting";

const MODIFIER_OVERRIDE_OPTIONS: { value: string; label: string }[] = [
  { value: FOLLOW_SETTING, label: "Follow the setting" },
  { value: EpocusBillingModifierOverride.None, label: "No modifier" },
  { value: EpocusBillingModifierOverride.Mod_26, label: "-26" },
];

const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Phoenix", label: "Arizona (Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
];

const timezoneOptions = (current: string) =>
  TIMEZONE_OPTIONS.some((option) => option.value === current)
    ? TIMEZONE_OPTIONS
    : [...TIMEZONE_OPTIONS, { value: current, label: current }];

const useSaveProgramSetup = (programId: string) => {
  const [updateProgram] = useEpocusUpdateProgramSetupMutation();
  const [saving, setSaving] = useState(false);

  const save = async (
    change: Omit<UpdateEpocusProgramInput, "programId">,
    label: string,
  ) => {
    setSaving(true);
    try {
      await updateProgram({ variables: { input: { programId, ...change } } });
      toast.success(`${label} saved`);
    } catch {
      toast.error(`Couldn't save the ${label.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  return { save, saving };
};

const RowRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 6,
});

const SetupRow: FCwC<{ label: string; description: string }> = ({
  label,
  description,
  children,
}) => (
  <RowRoot>
    <Typography
      style={{ fontSize: rem(15), fontWeight: 600, color: EPOCUS_TEXT_PRIMARY }}
    >
      {label}
    </Typography>
    <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
      {description}
    </Typography>
    {children}
  </RowRoot>
);

const SetupChoiceRow: FC<{
  label: string;
  description: string;
  options: { value: string; label: string }[];
  selected: string;
  canEdit: boolean;
  saving: boolean;
  onSelect: (value: string) => void;
}> = ({ label, description, options, selected, canEdit, saving, onSelect }) => (
  <SetupRow label={label} description={description}>
    {canEdit ? (
      <EnumChipSelect
        options={options}
        selected={selected}
        onChange={(next) => {
          if (next && next !== selected) onSelect(next);
        }}
        disabled={saving}
      />
    ) : (
      <Typography style={{ fontSize: rem(15), color: EPOCUS_TEXT_PRIMARY }}>
        {options.find((option) => option.value === selected)?.label ?? selected}
      </Typography>
    )}
  </SetupRow>
);

type MedicareLocality = NonNullable<
  NonNullable<EpocusProgramConfigQuery["programConfig"]>["medicareLocality"]
>;

const POSTAL_CODE = /^[A-Z0-9][A-Z0-9 -]{1,8}[A-Z0-9]$/;

const normalisePostalCode = (value: string): string =>
  value.trim().toUpperCase().replace(/\s+/g, " ");

const localityLine = (
  postalCode: string | null,
  locality: MedicareLocality | null,
): string => {
  if (!postalCode) {
    return "No postal code set, so the billing view shows no fee estimates.";
  }
  if (!locality) {
    return `${postalCode} is not a US ZIP in the CMS ZIP code file, so it has no Medicare locality.`;
  }

  const name = [locality.name, locality.state].filter(Boolean).join(", ");
  const line = `Medicare locality: ${name || "unnamed"} (${locality.carrier}-${locality.locality})`;
  return locality.spansLocalities
    ? `${line}. This ZIP spans more than one locality; CMS prices it at this one without a ZIP+4.`
    : line;
};

const PostalCodeRow: FC<{
  postalCode: string | null;
  locality: MedicareLocality | null;
  canEdit: boolean;
  saving: boolean;
  onSave: (postalCode: string | null) => Promise<void>;
}> = ({ postalCode, locality, canEdit, saving, onSave }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? postalCode ?? "";
  const normalised = normalisePostalCode(value);
  const valid = normalised === "" || POSTAL_CODE.test(normalised);

  return (
    <SetupRow
      label="Postal code"
      description="The practice's postal code, e.g. a US ZIP code. A US ZIP sets the Medicare locality that fee estimates on the billing view are priced at."
    >
      {canEdit ? (
        <Box style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <TextField
            id="epocus-program-postal-code"
            label="Postal code"
            variant="outlined"
            size="small"
            value={value}
            error={!valid}
            helperText={
              valid ? undefined : "3-10 letters, digits, spaces or hyphens"
            }
            inputProps={{ maxLength: 10 }}
            InputLabelProps={{ shrink: value !== "" || undefined }}
            onChange={(event) => setDraft(event.target.value)}
            style={{ maxWidth: 180 }}
          />
          <Button
            color="primary"
            variant="outlined"
            disabled={saving || !valid || draft === null}
            onClick={async () => {
              await onSave(normalised || null);
              setDraft(null);
            }}
            style={{ minHeight: 40 }}
          >
            Save postal code
          </Button>
        </Box>
      ) : (
        <Typography style={{ fontSize: rem(15), color: EPOCUS_TEXT_PRIMARY }}>
          {postalCode ?? "Not set"}
        </Typography>
      )}
      <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
        {localityLine(postalCode, locality)}
      </Typography>
    </SetupRow>
  );
};

const TimezoneRow: FC<{
  timezone: string;
  canEdit: boolean;
  saving: boolean;
  onSave: (timezone: string) => void;
}> = ({ timezone, canEdit, saving, onSave }) => {
  const options = timezoneOptions(timezone);
  return (
    <SetupRow
      label="Time zone"
      description="The program's local time. The daily reminder email's send time is read in it."
    >
      {canEdit ? (
        <TextField
          id="epocus-program-timezone"
          select
          variant="outlined"
          size="small"
          value={timezone}
          disabled={saving}
          SelectProps={{ native: true }}
          inputProps={{ "aria-label": "Time zone" }}
          onChange={(event) => {
            if (event.target.value !== timezone) onSave(event.target.value);
          }}
          style={{ maxWidth: 260 }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </TextField>
      ) : (
        <Typography style={{ fontSize: rem(15), color: EPOCUS_TEXT_PRIMARY }}>
          {options.find((option) => option.value === timezone)?.label ??
            timezone}
        </Typography>
      )}
    </SetupRow>
  );
};

const ReminderEmailTimeRow: FC<{
  reminderEmailTime: string;
  canEdit: boolean;
  saving: boolean;
  onSave: (reminderEmailTime: string) => Promise<void>;
}> = ({ reminderEmailTime, canEdit, saving, onSave }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? reminderEmailTime;

  return (
    <SetupRow
      label="Reminder email time"
      description="When performers with unfinished studies get their daily email, in the program's time zone. A performer can pick their own time in Settings."
    >
      {canEdit ? (
        <Box style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <TextField
            id="epocus-program-reminder-email-time"
            label="Send at"
            type="time"
            variant="outlined"
            size="small"
            value={value}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setDraft(event.target.value)}
            style={{ maxWidth: 180 }}
          />
          <Button
            color="primary"
            variant="outlined"
            disabled={saving || !value || draft === null}
            onClick={async () => {
              await onSave(value);
              setDraft(null);
            }}
            style={{ minHeight: 40 }}
          >
            Save time
          </Button>
        </Box>
      ) : (
        <Typography style={{ fontSize: rem(15), color: EPOCUS_TEXT_PRIMARY }}>
          {reminderEmailTime}
        </Typography>
      )}
    </SetupRow>
  );
};

export const ProgramSetupCard: FC<{
  programId: string;
  config: EpocusProgramConfigQuery["programConfig"] | undefined;
}> = ({ programId, config }) => {
  const canEdit = useIsEpocusPlatformAdmin();
  const { save, saving } = useSaveProgramSetup(programId);
  const billingCodesEnabled = useBillingCodesEnabled();
  if (!config) return null;

  return (
    <SettingsCard>
      <SettingsCardLabel>Program setup</SettingsCardLabel>
      {!canEdit && (
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
          Set by Elephant when your program was set up. Contact Elephant to
          change it.
        </Typography>
      )}
      <SetupChoiceRow
        label="Worksheet set"
        description="The worksheets performers choose from when they start a new exam. Exams already recorded keep their worksheet."
        options={WORKSHEET_SET_OPTIONS}
        selected={config.worksheetSet}
        canEdit={canEdit}
        saving={saving}
        onSelect={(next) =>
          void save(
            { worksheetSet: next as EpocusWorksheetSet },
            "Worksheet set",
          )
        }
      />
      {billingCodesEnabled && (
        <>
          <SetupChoiceRow
            label="Clinical setting"
            description="Where studies are performed. Office bills globally with no modifier. Hospital outpatient, ED and ASC (ambulatory surgery center) add -26 to codes that split into professional and technical parts."
            options={CLINICAL_SETTING_OPTIONS}
            selected={config.clinicalSetting}
            canEdit={canEdit}
            saving={saving}
            onSelect={(next) =>
              void save(
                { clinicalSetting: next as EpocusProgramClinicalSetting },
                "Clinical setting",
              )
            }
          />
          <SetupChoiceRow
            label="Billing modifier"
            description="Performers see this modifier next to each ticked code and cannot change it."
            options={MODIFIER_OVERRIDE_OPTIONS}
            selected={config.billingModifierOverride ?? FOLLOW_SETTING}
            canEdit={canEdit}
            saving={saving}
            onSelect={(next) =>
              void save(
                {
                  billingModifierOverride:
                    next === FOLLOW_SETTING
                      ? null
                      : (next as EpocusBillingModifierOverride),
                },
                "Billing modifier",
              )
            }
          />
          <PostalCodeRow
            postalCode={config.postalCode ?? null}
            locality={config.medicareLocality ?? null}
            canEdit={canEdit}
            saving={saving}
            onSave={(postalCode) => save({ postalCode }, "Postal code")}
          />
          <TimezoneRow
            timezone={config.timezone}
            canEdit={canEdit}
            saving={saving}
            onSave={(timezone) => void save({ timezone }, "Time zone")}
          />
          <ReminderEmailTimeRow
            reminderEmailTime={config.reminderEmailTime}
            canEdit={canEdit}
            saving={saving}
            onSave={(reminderEmailTime) =>
              save({ reminderEmailTime }, "Reminder email time")
            }
          />
        </>
      )}
    </SettingsCard>
  );
};
